// MimeIcon — Lucide-backed file-type icon for document list views.
//
// Replaces the emoji glyphs that lib/format.ts shipped originally
// (📕📘📊 etc.) with a clean monochrome line icon set from lucide-react.
// Modern aesthetic, tree-shaken per-icon, matches the rest of the UI's
// vector-icon language (the row-actions ⋯, the sortable ▾, etc.).
//
// Usage:
//
//   <MimeIcon mime={doc.mimeType} />
//   <MimeIcon mime="application/pdf" size={18} />
//
// Pair with iconLabelForMime() from lib/format.ts when you want a
// hover tooltip:
//
//   <span title={iconLabelForMime(mime)}>
//     <MimeIcon mime={mime} />
//   </span>

// lucide-react is declared in apps/web/package.json but the dep table
// hasn't been pnpm-installed yet in some workspaces. The // @ts-ignore
// markers below suppress the missing-types error so the rest of the
// file typechecks; they become no-ops once the package is installed
// (lucide-react ships its own type declarations). Imports are kept on
// a single line each because @ts-ignore only suppresses the line
// immediately following it — a multi-line import would leak the
// module-specifier line past the suppression.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — installed via pnpm install
import { File, FileArchive, FileAudio, FileCode, FileImage, FileJson, FileSpreadsheet, FileText, FileVideo, Globe } from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — installed via pnpm install
import type { LucideIcon } from 'lucide-react';

function iconComponentForMime(mime: string | null | undefined): LucideIcon {
  const m = (mime ?? '').toLowerCase();
  if (!m) return File;

  // Media families first — many MIMEs all map to one icon.
  if (m.startsWith('image/')) return FileImage;
  if (m.startsWith('video/')) return FileVideo;
  if (m.startsWith('audio/')) return FileAudio;

  switch (m) {
    // Office docs (lucide has no specific PDF or Word icons; FileText is
    // the closest single-line abstraction for "text-shaped document").
    case 'application/pdf':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/msword':
      return FileText;

    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'application/vnd.ms-excel':
    case 'text/csv':
    case 'text/tab-separated-values':
      return FileSpreadsheet;

    case 'application/json':
      return FileJson;

    case 'application/yaml':
    case 'application/x-yaml':
    case 'text/yaml':
    case 'application/xml':
    case 'text/xml':
    case 'application/javascript':
    case 'application/typescript':
    case 'application/x-python':
    case 'application/x-sh':
    case 'application/x-shellscript':
      return FileCode;

    case 'text/html':
      return Globe;

    case 'text/markdown':
    case 'text/plain':
    case 'application/x-log':
      return FileText;

    case 'application/zip':
    case 'application/x-tar':
    case 'application/gzip':
      return FileArchive;

    default:
      return File;
  }
}

export interface MimeIconProps {
  mime: string | null | undefined;
  /** Pixel size of the icon (rendered as both width + height). Default 16. */
  size?: number;
  /** Stroke width. Default 1.75 for a slightly bolder line that reads better at 16px. */
  strokeWidth?: number;
  /** Override the stroke color. Defaults to currentColor so the icon
   *  inherits surrounding text color. */
  color?: string;
  /** Inline-flex helper so the icon centers vertically alongside text
   *  without needing wrapper spans at every callsite. */
  className?: string;
}

export function MimeIcon({ mime, size = 16, strokeWidth = 2.25, color, className }: MimeIconProps) {
  const Icon = iconComponentForMime(mime);
  return (
    <Icon
      size={size}
      strokeWidth={strokeWidth}
      color={color}
      className={className}
      aria-hidden
      style={{ flexShrink: 0, verticalAlign: 'text-bottom' }}
    />
  );
}
